import { useMemo } from 'react';
import { useFeed } from './useFeed';
import { useListDocs } from './useListDocs';
import { subscribeChecklistItems, subscribeChecklistLog } from '../data/checklists';
import { DELETED_DOC_ID } from '../config/constants';

const NO_ROWS = [];
const NO_IDS = new Set();

/**
 * Live items + completion log of one standing checklist (Shopping / Backup / Cutting).
 *   confirmedEmpty — the SERVER says the list has no items at all. An empty first load
 *                    from the cache (offline, over quota) or a failed one doesn't count:
 *                    only a confirmed-empty list may get the template seeded.
 * History rows deleted on another device are left out (their tombstones — see
 * data/checklists.js removeChecklistLog).
 */
export function useChecklist(listId) {
  const items = useFeed(`checklist:${listId}`,
    (onData, onError) => subscribeChecklistItems(listId, onData, onError), { initial: NO_ROWS });
  const log = useFeed(`log:${listId}`,
    (onData, onError) => subscribeChecklistLog(listId, onData, onError), { initial: NO_ROWS });
  const deleted = useDeletedLogRows();
  const liveLog = useMemo(
    () => (deleted.size ? log.data.filter(r => !deleted.has(r.id)) : log.data), [log.data, deleted]);
  const confirmedEmpty = !items.loading && !items.error && items.data.length === 0
    && !!items.meta && items.meta.fromCache === false;
  return { items: items.data, log: liveLog, loading: items.loading, confirmedEmpty };
}

/** Ids of checklist-history rows deleted on any device (opsLists/_deleted — the lists feed already has it). */
function useDeletedLogRows() {
  const { docs } = useListDocs();
  const tomb = docs.find(d => d.id === DELETED_DOC_ID);   // same object while unchanged (data/rows.js)
  return useMemo(
    () => (tomb && tomb.checklistLog ? new Set(Object.keys(tomb.checklistLog)) : NO_IDS), [tomb]);
}
